import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ChevronLeft, LoaderCircle, RefreshCw } from 'lucide-react';
import { TaxBreakdown } from '../../components/ui/TaxBreakdown';
import { useUserProfile } from '../../context/UserProfileContext';
import { useSharedQuote } from '../../context/QuoteContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useOrgRates } from '../../hooks/useOrgRates';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { ReadOnlyBanner } from '../../components/ui/ReadOnlyBanner';
import { currencySymbol, QuoteFormula } from '../../types/admin';
import {
  buildCrewFromFormula,
  buildCrewFromOrgRates,
  computeFormula,
  getShiftLengthHours,
  getShuttleTrucksAndDrivers,
} from '../../utils/formulaCalculation';
import { LogisticsData } from '../../types/admin';
import { resolveOrgTravelTimeRate, resolveOrgEfficiencyMultiplier, resolveOrgFixedTravelTimeHours } from '@iomi/shared';

// ── Display helpers ───────────────────────────────────────────────────────────

/** Build a human-readable breakdown of the crew-size formula for a given leg. */
function crewSizeMathSub(logistics?: LogisticsData, isInternal?: boolean): string {
  const baseline = isInternal ? 4 : 3;
  if (!logistics) return `${baseline}`;
  const parts: string[] = [`${baseline}`];
  const totalStrides = (logistics.strides1 ?? 0) + (logistics.strides2 ?? 0);
  const stridesValue = totalStrides * 0.06;
  if (stridesValue > 0) {
    parts.push(`${fmt(stridesValue)} (${totalStrides} total strides × 0.06)`);
  }
  if ((logistics.stories ?? 0) > 0) {
    const storiesValue = (logistics.stories ?? 0) * 9;
    parts.push(`${storiesValue} (${logistics.stories} ${logistics.stories === 1 ? 'story' : 'stories'} × 9)`);
  }
  if (logistics.waypoint === 'Elevator') {
    parts.push('1 (elevator adj.)');
  }
  if (!logistics.loadingDock) {
    parts.push('2 (no loading dock)');
  }
  return parts.join(' + ');
}

function fmt(n: number, decimals = 2): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: decimals });
}

function fmtHours(n: number): string {
  return `${fmt(n)} hr${n !== 1 ? 's' : ''}`;
}

// ── Formula row ───────────────────────────────────────────────────────────────

function FormulaRow({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
}) {
  return (
    <div className={`flex items-start justify-between gap-4 py-2.5
      ${highlight ? 'bg-plum/5 -mx-4 px-4 rounded-lg' : ''}`}
    >
      <div className="flex-1 min-w-0">
        <p className={`text-small ${highlight ? 'font-semibold text-midnight' : 'font-medium text-midnight'}`}>
          {label}
        </p>
        {sub && <p className="text-[11px] text-slate mt-0.5 leading-snug">{sub}</p>}
      </div>
      <p className={`shrink-0 text-small font-bold tabular-nums ${highlight ? 'text-plum' : 'text-midnight'}`}>
        {value}
      </p>
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-stone -mx-4" />;
}

// ── Formula card ──────────────────────────────────────────────────────────────

function FormulaCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3.5 border-b border-stone">
        <p className="font-semibold text-small text-midnight">{title}</p>
      </div>
      <div className="px-4 py-3 flex flex-col gap-0">
        {children}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FormulaDetailsPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const { quote, loading: quoteLoading, updateQuote } = useSharedQuote();
  const { orgItems, orgItemsLoading } = useSharedQuote();
  const { org } = useOrgSettings(orgId ?? '');
  const { rates: orgRates, loading: orgRatesLoading } = useOrgRates(orgId ?? '');

  const [recalculating, setRecalculating] = useState(false);
  const [recalcError, setRecalcError] = useState('');

  async function handleRecalculate() {
    if (!quote || orgItemsLoading || orgRatesLoading) return;
    setRecalculating(true);
    setRecalcError('');
    try {
      const effectiveCrew = quote.crew ?? buildCrewFromOrgRates(orgRates);
      const effectiveMultiplier = quote.efficiencyMultiplier ?? resolveOrgEfficiencyMultiplier(org, quote.type);
      const formula = computeFormula(quote, orgItems, quote.travelTimeRate ?? resolveOrgTravelTimeRate(org, quote.type), effectiveCrew, effectiveMultiplier, org?.useBlendedTruckDriverRate, resolveOrgFixedTravelTimeHours(org, quote.type));
      const patch: Parameters<typeof updateQuote>[0] = { formula };
      if (!quote.crew && orgRates.length > 0) {
        patch.crew = effectiveCrew;
      }
      if (quote.efficiencyMultiplier === undefined) {
        patch.efficiencyMultiplier = effectiveMultiplier;
      }
      await updateQuote(patch);
    } catch (err) {
      setRecalcError(err instanceof Error ? err.message : 'Failed to recalculate');
    } finally {
      setRecalculating(false);
    }
  }

  const loading = quoteLoading || (orgItemsLoading && !quote?.formula);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <p className="text-small text-rose font-semibold">Quote not found</p>
      </div>
    );
  }

  const f: QuoteFormula | undefined = quote.formula;
  const timing = quote.movingDate?.timing;
  const shiftHours = quote.movingDate?.shiftLengthHours ?? getShiftLengthHours(timing);
  const symbol = currencySymbol(org?.defaultCurrency);
  const effectiveTravelTimeRate = quote.travelTimeRate ?? resolveOrgTravelTimeRate(org, quote.type);
  const displayMultiplier = f?.efficiencyMultiplier ?? quote.efficiencyMultiplier ?? resolveOrgEfficiencyMultiplier(org, quote.type);
  const isResidential = quote.type === 'residential';

  return (
    <div className="px-4 pt-6 pb-10 md:max-w-5xl md:mx-auto">
      {/* Back — hidden on md+ (shell left panel provides navigation) */}
      <button
        type="button"
        onClick={() => navigate(`/moves/${quoteId}`)}
        className="md:hidden flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        {quote.customerCompanyName}
      </button>

      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="font-display text-h1 text-midnight">Formula Details</h1>
          {f && (
            <p className="text-[12px] text-slate mt-0.5">
              Calculated {new Date(f.calculatedAt).toLocaleString('en-US', {
                month: 'short', day: 'numeric',
                hour: 'numeric', minute: '2-digit',
              })}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleRecalculate}
          disabled={recalculating || orgItemsLoading || orgRatesLoading || quote.quoteStatus !== 'draft'}
          className="shrink-0 flex items-center gap-1.5 h-9 px-3 rounded-xl border border-stone
                     text-[13px] font-semibold text-slate bg-white
                     hover:border-plum hover:text-plum active:bg-mist
                     disabled:opacity-50 transition-colors duration-150"
        >
          {recalculating
            ? <LoaderCircle size={14} className="animate-spin" />
            : <RefreshCw size={14} />
          }
          Recalculate
        </button>
      </div>

      {quote.quoteStatus !== 'draft' && <ReadOnlyBanner status={quote.quoteStatus} />}

      {recalcError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{recalcError}</p>
        </div>
      )}

      {!f ? (
        <div className="flex flex-col items-center py-16 gap-4 text-center">
          <p className="text-small font-medium text-midnight">No formula calculated yet</p>
          <p className="text-[13px] text-slate leading-snug max-w-xs">
            The formula runs automatically on the Quote Details page once {isResidential ? 'rooms' : 'inventory'},
            addresses, move timing, and strides are all set.
          </p>
        </div>
      ) : f.isFlatRate ? (
        /* ── Flat Rate Formula Details ── */
        <div className="flex flex-col gap-3 md:grid md:grid-cols-2 md:gap-4 md:items-start">
          <div className="flex flex-col gap-3">
            <FormulaCard title="Flat Rate Pricing">
              <FormulaRow
                label="Bedroom Count"
                sub="Number of bedrooms for this move"
                value={String(f.flatRateBedroomCount ?? '—')}
              />
              <Divider />
              <FormulaRow
                label="Room Tier"
                sub="Matched from organization rate card"
                value={f.flatRateRoomLabel ?? '—'}
              />
              <Divider />
              <FormulaRow
                label="Base Room Price"
                sub="Flat rate for this room tier"
                value={`${symbol}${fmt(f.flatRateRoomPrice ?? 0, 0)}`}
                highlight
              />
            </FormulaCard>

            {(f.flatRateAddons && f.flatRateAddons.length > 0) && (
              <FormulaCard title="Add-on Adjustments">
                {f.flatRateAddons.map((addon, i) => (
                  <div key={addon.slug}>
                    {i > 0 && <Divider />}
                    <FormulaRow
                      label={addon.label}
                      value={`${addon.amount >= 0 ? '+' : ''}${symbol}${fmt(addon.amount, 0)}`}
                    />
                  </div>
                ))}
              </FormulaCard>
            )}

            <FormulaCard title="Scheduling Details">
              <FormulaRow
                label="Recommended Crew Size"
                value={String(f.finalCrewSize)}
              />
              <Divider />
              <FormulaRow
                label="Recommended Trucks"
                value={String(f.truckQuantity)}
              />
              <Divider />
              <FormulaRow
                label="Estimated Duration"
                sub="On-site hours from room tier"
                value={fmtHours(f.actualJobDuration)}
              />
            </FormulaCard>
          </div>

          <div className="flex flex-col gap-3">
            <FormulaCard title="Travel & Transit">
              <FormulaRow
                label="Travel Time to Job"
                sub={effectiveTravelTimeRate === 'portal_to_portal' ? 'Portal to Portal' : '1 Hr Standard'}
                value={fmtHours(f.travelTimeToJobHours)}
              />
              <Divider />
              <FormulaRow
                label="Transit Time"
                sub="Driving between origin and destination is already in the on-site hours (Drive Minutes), so no separate transit leg is billed"
                value={fmtHours(f.finalTransitTimeHours)}
              />
              <Divider />
              <FormulaRow
                label="Total Hours"
                sub="On-site Hours + Travel Time to Job + Transit Time"
                value={fmtHours(f.totalHours)}
                highlight
              />
            </FormulaCard>

            <FormulaCard title="Cost Summary">
              <FormulaRow
                label="Base Room Price"
                value={`${symbol}${fmt(f.flatRateRoomPrice ?? 0, 0)}`}
              />
              {(f.flatRateAddons ?? []).map(addon => (
                <div key={addon.slug}>
                  <Divider />
                  <FormulaRow
                    label={addon.label}
                    value={`${addon.amount >= 0 ? '+' : ''}${symbol}${fmt(addon.amount, 0)}`}
                  />
                </div>
              ))}
              <Divider />
              <FormulaRow
                label="Total Flat Rate"
                value={`${symbol}${fmt(f.flatRateTotal ?? f.finalCostEstimate, 0)}`}
                highlight
              />
            </FormulaCard>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3 md:grid md:grid-cols-2 md:gap-4 md:items-start">

          {/* ── Left column: Crew Sizing + Labor ── */}
          <div className="flex flex-col gap-3">

            {/* ── Crew Sizing — residential vs commercial ── */}
            {isResidential ? (
              <FormulaCard title="Crew Sizing (Residential)">
                <FormulaRow
                  label="Total Labor Points"
                  sub="Σ (moveUnits + disassembly penalty 1.5 + bulky penalty 0.5) × qty"
                  value={fmt(f.totalLaborPoints ?? 0)}
                />
                <Divider />
                <FormulaRow
                  label="Crew Size Tier"
                  sub="<80 pts → 2, <200 → 3, <400 → 4, ≥400 → 5"
                  value={String(f.crewSizeTier ?? 0)}
                />
                <Divider />
                <FormulaRow
                  label="Access Bump"
                  sub="+ 1 crew member if origin or destination has 3+ stories without elevator"
                  value={f.accessBumpApplied ? 'Yes (+1)' : 'No'}
                />
                <Divider />
                <FormulaRow
                  label="Final Crew Size"
                  sub="Crew Tier + Access Bump"
                  value={String(f.finalCrewSize)}
                  highlight
                />
                <Divider />
                <FormulaRow
                  label="Trucks"
                  sub={`max(⌈volume⌉, ⌈weight⌉, 1) — by volume: ${f.trucksByVolume ?? 0}, by weight: ${f.trucksByWeight ?? 0}`}
                  value={String(f.truckQuantity)}
                />
              </FormulaCard>
            ) : (
              <FormulaCard title="Crew Sizing">
                <FormulaRow
                  label="Origin Optimum Crew Size"
                  sub={crewSizeMathSub(quote.originLogistics, quote.addresses?.internalMove)}
                  value={fmt(f.originOptimumCrewSize)}
                />
                <Divider />
                <FormulaRow
                  label="Destination Optimum Crew Size"
                  sub={quote.addresses?.internalMove
                    ? '0 — internal move: no destination leg'
                    : crewSizeMathSub(quote.destinationLogistics)}
                  value={fmt(f.destinationOptimumCrewSize)}
                />
                <Divider />
                <FormulaRow
                  label="Combined Optimum Crew Size"
                  sub="Origin + Destination"
                  value={fmt(f.combinedOptimumCrewSize)}
                  highlight
                />
                {f.method === 'Shuttle' && (() => {
                  const transitSec = quote.addresses?.transit?.durationSeconds ?? 0;
                  const shuttle = getShuttleTrucksAndDrivers(transitSec);
                  return (
                    <>
                      <Divider />
                      <FormulaRow
                        label="Shuttle Drivers"
                        sub={`Shuttle transit lookup — ${Math.ceil((transitSec / 60) / 15) * 15} min bracket → ${shuttle.drivers} driver${shuttle.drivers !== 1 ? 's' : ''}`}
                        value={String(shuttle.drivers)}
                      />
                    </>
                  );
                })()}
                <Divider />
                <FormulaRow
                  label="Calculated Actual Crew Size"
                  sub="Total Man-hours ÷ Shift Length, capped at min(Origin, Destination)"
                  value={fmt(f.approxCrewSize)}
                />
                <Divider />
                <FormulaRow
                  label="Final Crew Size"
                  sub={f.truckloads >= 7
                    ? `Truckloads (${fmt(f.truckloads)}) ≥ 7 → Combined Optimum Crew Size (${fmt(f.combinedOptimumCrewSize)}) + Shuttle Drivers (${getShuttleTrucksAndDrivers(quote.addresses?.transit?.durationSeconds ?? 0).drivers}) for the most efficient use of resources`
                    : '⌈Calculated Actual Crew Size⌉ — same as calculated unless manually adjusted on the Breakdown page'}
                  value={String(f.finalCrewSize)}
                  highlight
                />
                <Divider />
                <FormulaRow
                  label="Trucks"
                  sub={f.method === 'Internal'
                    ? 'Internal move — no trucks needed'
                    : f.method === 'Shuttle'
                      ? `Shuttle method — quantity based on transit time (${Math.round((quote.addresses?.transit?.durationSeconds ?? 0) / 60)} min)`
                      : `Load & Go method — ⌈Truckloads ÷ shifts⌉, min 1 (shifts = ⌈Actual Job Duration ÷ Shift Length⌉ = ${shiftHours > 0 ? Math.max(1, Math.ceil(f.actualJobDuration / shiftHours)) : 1})`}
                  value={String(f.truckQuantity)}
                />
              </FormulaCard>
            )}

            {/* ── Crew Composition (read-only) ── */}
            {(() => {
              const crewWithQty = buildCrewFromFormula(f, quote.crew ?? [], quote.type, org?.useBlendedTruckDriverRate);
              if (crewWithQty.length === 0) return null;
              return (
                <FormulaCard title="Crew Composition">
                  {crewWithQty.map((m, i) => (
                    <div key={m.slug}>
                      {i > 0 && <Divider />}
                      <FormulaRow
                        label={m.name}
                        value={`${m.quantity} × ${symbol}${fmt(m.hourlyRate, 2)}/h`}
                      />
                    </div>
                  ))}
                  <Divider />
                  <FormulaRow
                    label="Total Hourly Rate"
                    value={`${symbol}${fmt(crewWithQty.reduce((sum, m) => sum + m.quantity * m.hourlyRate, 0), 2)}/h`}
                    highlight
                  />
                </FormulaCard>
              );
            })()}

            {/* ── Labor — switches between residential and commercial ── */}
            {isResidential ? (
              <>
                {/* ── Inventory Totals ── */}
                <FormulaCard title="Inventory Totals">
                  <FormulaRow
                    label="Total Cubic Feet"
                    sub="Σ (item cubic feet × qty) across all rooms"
                    value={fmt(f.totalCubicFeet ?? 0)}
                  />
                  <Divider />
                  <FormulaRow
                    label="Total Weight"
                    sub="Σ (item weight × qty) across all rooms"
                    value={`${fmt(f.totalWeight ?? 0)} lbs`}
                  />
                  <Divider />
                  <FormulaRow
                    label="Total Move Units"
                    sub="Σ (item move units × qty) across all rooms"
                    value={fmt(f.totalMoveUnits ?? 0)}
                  />
                  <Divider />
                  <FormulaRow
                    label="Bulky Items"
                    sub="Count of items flagged as bulky (+ 5 min each, + 0.5 labor pts)"
                    value={String(f.bulkyItemCount ?? 0)}
                  />
                  <Divider />
                  <FormulaRow
                    label="Disassembly Items"
                    sub="Count of items requiring disassembly (+ 15 min each, + 1.5 labor pts)"
                    value={String(f.disassemblyItemCount ?? 0)}
                  />
                </FormulaCard>

                {/* ── Truck Calculation ── */}
                <FormulaCard title="Truck Calculation">
                  <FormulaRow
                    label="Usable Cubic Feet / Truck"
                    sub={`${fmt(1700)} gross capacity × ${fmt(0.80, 2)} load efficiency`}
                    value={`${fmt(f.usableCubicFeet ?? 1360)} cf`}
                  />
                  <Divider />
                  <FormulaRow
                    label="Trucks by Volume"
                    sub={`⌈${fmt(f.totalCubicFeet ?? 0)} cf ÷ ${fmt(f.usableCubicFeet ?? 1360)} cf⌉`}
                    value={String(f.trucksByVolume ?? 0)}
                  />
                  <Divider />
                  <FormulaRow
                    label="Trucks by Weight"
                    sub={`⌈${fmt(f.totalWeight ?? 0)} lbs ÷ 10,000 lbs⌉`}
                    value={String(f.trucksByWeight ?? 0)}
                  />
                  <Divider />
                  <FormulaRow
                    label="Final Truck Count"
                    sub="max(by volume, by weight, 1)"
                    value={String(f.truckQuantity)}
                    highlight
                  />
                  {f.recommendedTruckTypes && f.recommendedTruckTypes.length > 0 && (
                    <>
                      <Divider />
                      <div className="px-1 py-2">
                        <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">Recommended Truck Types</p>
                        <div className="flex flex-col gap-1.5">
                          {f.recommendedTruckTypes.map((rt) => (
                            <div key={rt.type} className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-mist/50">
                              <span className="text-[13px] text-midnight font-medium capitalize">
                                {rt.type.replace(/_/g, ' ')}
                              </span>
                              <span className="text-[13px] text-slate">
                                {rt.count}× &middot; {fmt(rt.capacityPerTruck)} cf each
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </FormulaCard>

                {/* ── Labor Hours ── */}
                <FormulaCard title="Labor Hours (Residential)">
                  <FormulaRow
                    label="Total Item Minutes"
                    sub="Σ (base min by tier + disassembly 15 min + bulky 5 min) × qty"
                    value={`${fmt(f.totalItemMinutes ?? 0)} min`}
                  />
                  <Divider />
                  <FormulaRow
                    label="Base Load Minutes"
                    sub={`Total Item Minutes ÷ ${f.finalCrewSize} crew (parallel work)`}
                    value={`${fmt(f.baseLoadMinutes ?? 0)} min`}
                  />
                  <Divider />
                  <FormulaRow
                    label="Origin Access Multiplier"
                    sub={`1.0${(f.originFlights ?? 0) > 0 ? ` + ${f.originFlights} flights × 0.12` : ''}${quote.addresses?.origin?.residentialProperty?.elevator ? ' + elevator 0.05' : ''}${quote.addresses?.origin?.residentialProperty?.longCarry ? ' + long carry 0.15' : ''}`}
                    value={`×${fmt(f.originAccessMultiplier ?? 1, 2)}`}
                  />
                  <Divider />
                  <FormulaRow
                    label="Load Minutes"
                    sub="Base Load Minutes × Origin Access Multiplier"
                    value={`${fmt(f.loadMinutes ?? 0)} min`}
                    highlight
                  />
                  <Divider />
                  <FormulaRow
                    label="Dest. Access Multiplier"
                    sub={`1.0${(f.destFlights ?? 0) > 0 ? ` + ${f.destFlights} flights × 0.12` : ''}${quote.addresses?.destination?.residentialProperty?.elevator ? ' + elevator 0.05' : ''}${quote.addresses?.destination?.residentialProperty?.longCarry ? ' + long carry 0.15' : ''}`}
                    value={`×${fmt(f.destAccessMultiplier ?? 1, 2)}`}
                  />
                  <Divider />
                  <FormulaRow
                    label="Unload Minutes"
                    sub="Base Load Minutes × 0.70 unload factor × Dest. Access Multiplier"
                    value={`${fmt(f.unloadMinutes ?? 0)} min`}
                    highlight
                  />
                  <Divider />
                  <FormulaRow
                    label="Drive Minutes"
                    sub="Transit time between origin and destination"
                    value={`${fmt(f.driveMinutes ?? 0)} min`}
                  />
                  <Divider />
                  <FormulaRow
                    label="Overhead"
                    sub="Walkthrough, paperwork, truck setup, breaks"
                    value={`${fmt(f.overheadMinutes ?? 45)} min`}
                  />
                  <Divider />
                  <FormulaRow
                    label="Total Minutes"
                    sub="Load + Drive + Unload + Overhead"
                    value={`${fmt(f.totalMinutes ?? 0)} min`}
                  />
                  <Divider />
                  <FormulaRow
                    label="On-site Hours"
                    sub="⌈Total Minutes ÷ 60⌉ rounded up to 15 min — the Actual Job Duration"
                    value={fmtHours(f.actualJobDuration)}
                    highlight
                  />
                </FormulaCard>
              </>
            ) : (
              <FormulaCard title="Labor">
                <FormulaRow
                  label="Total KZ"
                  sub="Σ (quantity × KZ) across all inventory items"
                  value={fmt(f.totalKZ)}
                />
                <Divider />
                <FormulaRow
                  label="Casegoods Man-hours"
                  sub="Casegoods KZ ÷ 700 × Combined Crew"
                  value={fmt(f.casegoodsManHours ?? 0)}
                />
                <Divider />
                <FormulaRow
                  label="Base Modular Man-hours"
                  sub="Modular KZ ÷ 700 × Combined Crew"
                  value={fmt(f.modularBaseManHours ?? 0)}
                />
                {(f.modularDisassemblyManHours != null && f.modularDisassemblyManHours > 0) && (
                  <>
                    <Divider />
                    <FormulaRow
                      label="Modular Disassembly Man-hours"
                      sub="Base Modular × 0.05"
                      value={fmt(f.modularDisassemblyManHours)}
                    />
                  </>
                )}
                {(f.modularReassemblyManHours != null && f.modularReassemblyManHours > 0) && (
                  <>
                    <Divider />
                    <FormulaRow
                      label="Modular Reassembly Man-hours"
                      sub="Base Modular × 0.30"
                      value={fmt(f.modularReassemblyManHours)}
                    />
                  </>
                )}
                {(f.modularBaseManHours != null && f.modularBaseManHours > 0 &&
                  ((f.modularDisassemblyManHours ?? 0) > 0 || (f.modularReassemblyManHours ?? 0) > 0)) && (
                  <>
                    <Divider />
                    <FormulaRow
                      label="Adjusted Modular Man-hours"
                      sub="Base + Disassembly + Reassembly"
                      value={fmt(f.modularManHours ?? 0)}
                    />
                  </>
                )}
                {(f.buildingProtectionManHours != null && f.buildingProtectionManHours > 0) && (
                  <>
                    <Divider />
                    <FormulaRow
                      label="Building Protection Man-hours"
                      sub={`⌈Truckloads (${fmt(f.truckloads)})⌉ − 6 = ${f.buildingProtectionManHours} (min 0)`}
                      value={fmt(f.buildingProtectionManHours)}
                    />
                  </>
                )}
                <Divider />
                <FormulaRow
                  label="Base Man-hours"
                  sub={`Casegoods + Modular + Additional${(f.buildingProtectionManHours ?? 0) > 0 ? ' + Building Protection' : ''} man-hours`}
                  value={fmt(f.baseManHours ?? (f.manHours / displayMultiplier))}
                  highlight
                />
                <Divider />
                <FormulaRow
                  label="Efficiency Multiplier"
                  sub="Quote-level setting (frozen from org default at first calculation)"
                  value={fmt(displayMultiplier, 2)}
                />
                <Divider />
                <FormulaRow
                  label="Total Man-hours"
                  sub={`Base Man-hours × ${fmt(displayMultiplier, 2)} efficiency multiplier`}
                  value={fmt(f.manHours)}
                  highlight
                />
                <Divider />
                <FormulaRow
                  label="Truckloads"
                  sub="Total KZ ÷ 700"
                  value={fmt(f.truckloads)}
                />
              </FormulaCard>
            )}

          </div>{/* end left column */}

          {/* ── Right column: Travel and Labor Time + Cost Summary ── */}
          <div className="flex flex-col gap-3">

            {/* ── Travel and Labor Time ── */}
            <FormulaCard title="Travel and Labor Time">
              <FormulaRow
                label="Actual Job Duration"
                sub="Total Man-hours ÷ Final Crew Size"
                value={fmtHours(f.actualJobDuration)}
                highlight
              />
              <Divider />
              <FormulaRow
                label="Move Method"
                sub={f.method === 'Internal'
                  ? 'Internal move — no trucks needed, same building'
                  : f.method === 'Shuttle'
                    ? `Truckloads (${fmt(f.truckloads)}) > 4 → Shuttle: trucks rotate between origin and destination`
                    : `Truckloads (${fmt(f.truckloads)}) ≤ 4 → Load & Go: all trucks travel together`}
                value={f.method}
              />
              <Divider />
              <FormulaRow
                label="Shift Length"
                sub={timing ? `Move timing: ${timing}` : 'No timing set — defaulting to 7 hrs'}
                value={fmtHours(shiftHours)}
              />
              <Divider />
              <FormulaRow
                label="Travel Time Rate Setting"
                sub="Quote-level setting (defaults to org setting)"
                value={effectiveTravelTimeRate === 'portal_to_portal' ? 'Portal to Portal' : '1 Hr Standard'}
              />
              <Divider />
              <FormulaRow
                label="Travel Time to Job"
                sub={effectiveTravelTimeRate === 'portal_to_portal'
                  ? 'Portal to Portal: origin warehouse + destination warehouse travel times'
                  : '1-hour standard'}
                value={fmtHours(f.travelTimeToJobHours)}
              />
              <Divider />
              <FormulaRow
                label="Actual Transit Time"
                sub="Driving time between origin and destination"
                value={quote.addresses?.transit?.durationSeconds
                  ? `${Math.round(quote.addresses.transit.durationSeconds / 60)} min`
                  : '—'}
              />
              <Divider />
              <FormulaRow
                label="Final Transit Time"
                sub="Transit < 1 hr → 0; otherwise Transit − 1 hr"
                value={fmtHours(f.finalTransitTimeHours)}
              />
              <Divider />
              <FormulaRow
                label="Total Hours"
                sub="Actual Job Duration + Travel Time + Final Transit Time"
                value={fmtHours(f.totalHours)}
                highlight
              />
            </FormulaCard>

            {/* ── Cost Summary ── */}
            <FormulaCard title="Cost Summary">
              {isResidential ? (
                <>
                  <FormulaRow
                    label="Crew Composition"
                    sub="1 supervisor (who drives) + helpers"
                    value={`1 sup + ${Math.max(0, f.finalCrewSize - 1)} mover${f.finalCrewSize - 1 !== 1 ? 's' : ''} + ${f.truckQuantity} truck${f.truckQuantity !== 1 ? 's' : ''}`}
                  />
                  <Divider />
                </>
              ) : null}
              <FormulaRow
                label="Total Hourly Rate"
                sub={isResidential
                  ? 'Supervisor + Movers + Trucks (no separate drivers)'
                  : 'Σ (crew quantity × hourly rate) across all resources'}
                value={`${symbol}${fmt(f.totalHourlyRate, 2)}/h`}
              />
              <Divider />
              {(f.materialsCost ?? 0) > 0 && (
                <>
                  <FormulaRow
                    label="Labor"
                    sub="Total Hourly Rate × Total Hours"
                    value={`${symbol}${fmt(f.finalCostEstimate - (f.materialsCost ?? 0), 0)}`}
                  />
                  <Divider />
                  <FormulaRow
                    label={`Materials (×${f.materialsCount ?? 0})`}
                    sub="Packing items + equipment at the move's rental length"
                    value={`${symbol}${fmt(f.materialsCost ?? 0, 0)}`}
                  />
                  <Divider />
                </>
              )}
              <FormulaRow
                label="Final Cost Estimate"
                sub={(f.materialsCost ?? 0) > 0 ? 'Labor + Materials' : 'Total Hourly Rate × Total Hours'}
                value={`${symbol}${fmt(f.finalCostEstimate, 0)}`}
                highlight
              />
            </FormulaCard>

            {/* ── Tax Breakdown ── */}
            {(quote.laborTaxRate != null || quote.requiresManualTaxReview) && f && (
              <FormulaCard title="Tax">
                <TaxBreakdown
                  subtotal={f.finalCostEstimate}
                  taxState={quote.taxState}
                  laborTaxRate={quote.laborTaxRate}
                  taxAmount={quote.taxAmount}
                  materialsTaxRate={quote.materialsTaxRate}
                  materialsCost={f.materialsCost}
                  totalWithTax={quote.taxAmount != null ? f.finalCostEstimate + quote.taxAmount : undefined}
                  requiresManualTaxReview={quote.requiresManualTaxReview}
                  symbol={symbol}
                />
              </FormulaCard>
            )}

          </div>{/* end right column */}

        </div>
      )}
    </div>
  );
}
